import { Typography, message } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  DELETE_PRODUCTION_HOUR,
  EXPORT_HOURLY_PRODUCTION,
  GET_HOURLY_PRODUCTION_LIST,
  VIEW_HOURLY_PRODUCTION,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import { FABRIC_REPORT_GENERATE_PERMISSION } from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";

const HourlyProduction = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Fix round 1: last window actually queried, for the A3 empty-state hint.
  const [queryWindow, setQueryWindow] = useState({
    start: moment().subtract(6, "days"),
    end: moment(),
  });

  // Fix round 1: guard against setState after unmount (cold review finding 2).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Final fix wave (C1): request-sequence ref — a fast manual Preview must never be
  // overwritten by a slower, stale mount request resolving afterwards.
  const reqIdRef = useRef(0);
  const lastFilterValuesRef = useRef(null);
  const [fetchError, setFetchError] = useState(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const cutPath = (path) => {
    const updatedPath = path ? path.split("public/hourly_production/")[1] : "";
    return updatedPath;
  };

  const getAllPorductionHour = useCallback(async () => {
    setLoading(true);
    const query = `${GET_HOURLY_PRODUCTION_LIST}`;

    const response = await getData(query);
    if (response?.status === 200) {
      setLoading(false);
      setTableData(response?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting production hour data");
    }
  }, []);

  const exportProductionHour = async (filterValues) => {
    if (Object.values(filterValues).some((val) => val)) {
      setLoading(true);
      const { start_date, end_date } = filterValues;
      const query = `${EXPORT_HOURLY_PRODUCTION}`;
      const bodyData = {
        ...(start_date && {
          ...filterValues,
          start_date: moment(start_date).format("YYYY-MM-DD"),
        }),
        ...(end_date && { end_date: moment(end_date).format("YYYY-MM-DD") }),
      };
      const response = await getData(
        query,
        false,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response && response?.data) {
        // Download the file
        const outputFilename = `${String(start_date)
          .split(" ")
          .join("-")}-${String(end_date).split(" ").join("-")}.xlsx`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setLoading(false);
        message.success("Production Hours Exported Successfully");
        refetch();
      } else {
        setLoading(false);
        message.error("Something went wrong");
      }
    }
  };

  const previewFunction = async (filterValues) => {
    // Final fix wave (C1): claim this request's sequence number before the await so a
    // later, faster request (e.g. the user's own manual Preview) can invalidate this one.
    const myReq = ++reqIdRef.current;
    lastFilterValuesRef.current = filterValues;
    setFetchError(null);
    setLoading(true);
    const { start_date, end_date } = filterValues || {};
    const bodyData = {
      ...(start_date && {
        start_date: moment(start_date).format("YYYY-MM-DD"),
      }),
      ...(end_date && { end_date: moment(end_date).format("YYYY-MM-DD") }),
    };
    // Fix round 1: track the window actually queried for the A3 empty-state hint.
    if (start_date || end_date) {
      setQueryWindow((prev) => ({
        start: start_date ? moment(start_date) : prev.start,
        end: end_date ? moment(end_date) : prev.end,
      }));
    }

    const query = `${VIEW_HOURLY_PRODUCTION}`;
    const response = await postData(query, bodyData);

    // Fix round 1: mounted guard (cold review finding 2).
    // Final fix wave (C1): also drop this response if a newer request has since been fired
    // (stale-response race — see FINAL-REVIEW.md).
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.code === 200) {
      setLoading(false);
      // Fix round 1 (finding 4): an empty window can return data_row as null/undefined
      // rather than []; this used to only run after a manual Preview click, but the mount
      // effect now exercises it on every visit, so guard the push instead of crashing.
      const rowData = Array.isArray(response?.data?.data_row)
        ? response.data.data_row
        : [];
      const sumRow = response?.data?.sum_row;
      if (sumRow) {
        rowData.push(sumRow);
      }
      setTableData(rowData);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  // Effects
  // Load with last 7 days ending today by default (matches the initialValue defaults in
  // common/SearchForm/FilterFields/FabricReconcilFilterFields.js, reused here via the
  // filterName="fabric_reconcil" bug noted below and in NOTE.md).
  useEffect(() => {
    previewFunction({
      start_date: moment().subtract(6, "days").format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedHour = async (name) => {
    setLoading(true);
    const updatedName = cutPath(name);
    const query = `${DELETE_PRODUCTION_HOUR}`;
    const bodyData = {
      name: updatedName,
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setLoading(false);
      message.success("Production Hour Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openFile = (name) => {
    const URL = `${process.env.REACT_APP_BASE_URL}/storage/hourly_production/${name}`;
    window.open(URL, "_blank", "noreferrer");
  };

  const formatInt = (value) => {
    if (typeof value === "number") {
      return value.toLocaleString(undefined, {
        maximumFractionDigits: 0,
        maximumSignificantDigits: 9,
      });
    }
    return value;
  };
  const compactWidth = 80;
  const tableColumns = [
    {
      title: "Sewing Hourly Production Report",
      children: [
        {
          title: "Sewing",
          children: [
            {
              title: "Line",
              dataIndex: ["line"],
              key: "line",
              width: compactWidth,
              render: formatInt,
            },
          ],
        },
        {
          title: "Target",
          children: [
            {
              title: "Plan",
              dataIndex: ["plan"],
              key: "plan",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "Actual",
              dataIndex: ["actual"],
              key: "actual",
              width: compactWidth,
              render: formatInt,
            },
          ],
        },
        {
          title: "Normal Hour Production",
          children: [
            {
              title: "1st HR",
              dataIndex: ["1st"],
              key: "1st",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "2nd HR",
              dataIndex: ["2nd"],
              key: "2nd",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "3rd HR",
              dataIndex: ["3rd"],
              key: "3rd",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "4th HR",
              dataIndex: ["4th"],
              key: "4th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "5th HR",
              dataIndex: ["5th"],
              key: "5th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "6th HR",
              dataIndex: ["6th"],
              key: "6th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "7th HR",
              dataIndex: ["7th"],
              key: "7th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "8th HR",
              dataIndex: ["8th"],
              key: "8th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "Total NH",
              dataIndex: ["total_n_h"],
              key: "total_n_h",
              width: compactWidth,
              render: formatInt,
            },
          ],
        },
        {
          title: "OT Hour Production",
          children: [
            {
              title: "9th HR",
              dataIndex: ["9th"],
              key: "9th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "10th HR",
              dataIndex: ["10th"],
              key: "10th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "11th HR",
              dataIndex: ["11th"],
              key: "11th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "12th HR",
              dataIndex: ["12th"],
              key: "12th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "13th HR",
              dataIndex: ["13th"],
              key: "13th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "14th HR",
              dataIndex: ["14th"],
              key: "14th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "15th HR",
              dataIndex: ["15th"],
              key: "15th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "16th HR",
              dataIndex: ["16th"],
              key: "16th",
              width: compactWidth,
              render: formatInt,
            },
            {
              title: "Total OT",
              dataIndex: ["total_ot"],
              key: "total_ot",
              width: compactWidth,
              render: formatInt,
            },
          ],
        },
        {
          title: "Total Production",
          dataIndex: ["total_production"],
          key: "total_production",
          width: compactWidth,
          render: formatInt,
        },
        {
          title: "Total Variance",
          dataIndex: ["variance"],
          key: "variance",
          width: compactWidth,
          render: formatInt,
        },
      ],
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={exportProductionHour}
        filterName={"fabric_reconcil"}
        searchButtonText={"Export"}
        defaultOpen
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        isPreview={true}
        previewFunction={previewFunction}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportProductionHour}
                hideAddButton={true}
                disabled={!hasPermission([FABRIC_REPORT_GENERATE_PERMISSION])}
                setShowFilter={setShowFilter}
                showFilter={showFilter}
              />
            }
            title={<Title level={5}>Hourly Production</Title>}
          />
        }
        content={
          <>
            <div className="table" style={{ marginTop: 10 }}>
              {memoizedCustomFilter}
              <ListTable
                size="small"
                loading={loading}
                error={fetchError}
                onRetry={() => previewFunction(lastFilterValuesRef.current)}
                dataSource={tableData}
                columns={[
                  {
                    ...tableColumns[0],
                    fixed: "left",
                    children: tableColumns[0].children.map((child) => {
                      if (child.title === "Sewing") {
                        return {
                          ...child,
                          children: child.children.map((subChild) => ({
                            ...subChild,
                            fixed: "left",
                          })),
                        };
                      }
                      return child;
                    }),
                  },
                ]}
                scroll={{
                  x: 1200,
                  y: 500,
                }}
                bordered
                sticky
                className="compact-table"
                locale={{
                  emptyText: `No records between ${queryWindow.start.format(
                    "DD-MMM-YYYY",
                  )} and ${queryWindow.end.format("DD-MMM-YYYY")}. Widen the range above.`,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default HourlyProduction;
